import { action, observable } from 'mobx';
import { isUndefined, mergeWith } from 'lodash';
import { IEntity } from '~/types/model.types';
import { TGlobalContext } from '~/types/app.types';
import { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';
import type { Constructor } from 'type-fest';

export abstract class BaseGlobalEntityStore<T extends IEntity, M> {
    @observable all: Record<string, M> = {};
    globalContext: TGlobalContext | AuthenticatedGlobalContextValue = null;
    modelClass: Constructor<M> = null;

    constructor(
        globalContext: TGlobalContext | AuthenticatedGlobalContextValue,
        modelClass: Constructor<M>
    ) {
        this.globalContext = globalContext;
        this.modelClass = modelClass;
    }

    @action
    update(item: Partial<T> & { _id: string }) {
        const newItemInstance = new this.modelClass(item, this.globalContext);

        const updatedItem = this.all[item._id]
            ? mergeWith(
                  this.all[item._id],
                  newItemInstance,
                  // Prefer new value over existing value.
                  // If both values are defined, take the new one
                  (existingVal, newVal) =>
                      isUndefined(newVal) ? existingVal : newVal
              )
            : newItemInstance;

        this.all[item._id] = updatedItem;

        return updatedItem;
    }

    byId(_id: string): M {
        return this.all[_id];
    }

    @action
    init() {
        this.all = {};
    }
}
